class CartDrawer extends HTMLElement {
	constructor() {
		super();

		this.addEventListener(
			"keyup",
			(evt) => evt.code === "Escape" && this.close(),
		);
		this.querySelector("#CartDrawer-Overlay").addEventListener(
			"click",
			this.close.bind(this, false),
		);
		this.setHeaderCartIconAccessibility();
		this.initCheckoutWithRefresh();

		const headerAccount = document.querySelector(".header__account");
		if (headerAccount) {
			headerAccount.addEventListener("click", () => {
				if (this.classList.contains("active")) {
					this.close();
				}
			});
		}

		const search = document.querySelector(".header__search");
		if (search) {
			search.addEventListener("click", () => {
				if (search.isOpen && this.classList.contains("active")) {
					this.close();
				}
			});
		}

		const localization = document.querySelectorAll(".header__localization");
		localization.forEach((localization) => {
			localization.addEventListener("click", () => {
				if (
					localization.querySelector("button[aria-expanded=true]") &&
					this.classList.contains("active")
				) {
					this.close();
				}
			});
		});
	}

	setHeaderCartIconAccessibility() {
		const cartLink = document.querySelector("#cart-icon-bubble");
		cartLink.setAttribute("role", "button");
		cartLink.setAttribute("aria-haspopup", "dialog");
		cartLink.addEventListener("click", (event) => {
			event.preventDefault();
			if (!this.classList.contains("active")) this.open(cartLink);
			else this.close();
		});
		cartLink.addEventListener("keydown", (event) => {
			if (event.code.toUpperCase() === "SPACE") {
				event.preventDefault();
				if (!this.classList.contains("active")) this.open(cartLink);
				else this.close();
			}
		});
	}

	initCheckoutWithRefresh() {
		this.addEventListener("click", (event) => {
			const link = event.target.closest("#CartDrawer-Checkout");
			if (!link || link.getAttribute("aria-disabled") === "true") return;
			event.preventDefault();
			if (typeof window.refreshCartBeforeCheckout !== "function") {
				window.location.href = window.routes?.cart_url || "/cart";
				return;
			}
			link.setAttribute("aria-busy", "true");
			window.refreshCartBeforeCheckout("drawer").then(() => {
				link.removeAttribute("aria-busy");
				window.location.href = window.routes?.cart_url || "/cart";
			});
		});
	}

	open(triggeredBy) {
		const search = document.querySelector(".header__search");
		if (search) {
			search.close();
		}
		const annBar = document.querySelector(".section-announcement");
		if (annBar) annBar.classList.add("section-announcement-overlay");

		if (triggeredBy) this.setActiveElement(triggeredBy);
		const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
		if (cartDrawerNote && !cartDrawerNote.hasAttribute("role"))
			this.setSummaryAccessibility(cartDrawerNote);

		// Double rAF guarantees the browser commits the initial transform
		// of a freshly-inserted .drawer__inner before we apply .active,
		// so the slide-in transition reliably fires on the first open.
		requestAnimationFrame(() => {
			requestAnimationFrame(() => {
				this.classList.add("animate", "active");
				document.body.classList.add("overflow-hidden-drawer");
			});
		});

		this.addEventListener(
			"transitionend",
			() => {
				const containerToTrapFocusOn = this.classList.contains("is-empty")
					? this.querySelector(".drawer__inner-empty")
					: document.getElementById("CartDrawer");
				const focusElement =
					this.querySelector(".drawer__inner") ||
					this.querySelector(".drawer__close");
				trapFocus(containerToTrapFocusOn, focusElement);

				const cartDrawerItems = this.querySelector('cart-drawer-items');
				if (cartDrawerItems && typeof cartDrawerItems.cartShipping === 'function') {
					setTimeout(() => {
						cartDrawerItems.cartShipping();
					}, 100);
				}
			},
			{ once: true },
		);
	}

	close() {
		const annBar = document.querySelector(".section-announcement");
		if (annBar) annBar.classList.remove("section-announcement-overlay");

		this.classList.remove("animate", "active");
		removeTrapFocus(this.activeElement);
		document.body.classList.remove("overflow-hidden-drawer");
	}

	setSummaryAccessibility(cartDrawerNote) {
		cartDrawerNote.setAttribute("role", "button");
		cartDrawerNote.setAttribute("aria-expanded", "false");

		if (cartDrawerNote.nextElementSibling.getAttribute("id")) {
			cartDrawerNote.setAttribute(
				"aria-controls",
				cartDrawerNote.nextElementSibling.id,
			);
		}

		cartDrawerNote.addEventListener("click", (event) => {
			event.currentTarget.setAttribute(
				"aria-expanded",
				!event.currentTarget.closest("details").hasAttribute("open"),
			);
		});

		cartDrawerNote.parentElement.addEventListener("keyup", onKeyUpEscape);
	}

	renderContents(parsedState) {
		const drawerInner = this.querySelector(".drawer__inner");
		if (drawerInner?.classList.contains("is-empty")) {
			drawerInner.classList.remove("is-empty");
		}

		this.productId = parsedState.id;
		let sectionsUpdated = false;

		this.getSectionsToRender().forEach((section) => {
			const sectionElement = section.selector
				? document.querySelector(section.selector)
				: document.getElementById(section.id);
			const sectionHtml = parsedState.sections?.[section.id];
			if (!sectionElement || !sectionHtml) return;

			const innerHTML = this.getSectionInnerHTML(sectionHtml, section.selector);
			if (!innerHTML) return;

			sectionElement.innerHTML = innerHTML;
			sectionsUpdated = true;
		});

		if (!sectionsUpdated) return;

		// innerHTML replaced #CartDrawer-Overlay, so its constructor-bound
		// click listener is gone. Re-bind it before opening.
		const overlay = this.querySelector("#CartDrawer-Overlay");
		if (overlay) {
			overlay.addEventListener("click", this.close.bind(this, false));
		}

		// Force layout on the new .drawer__inner so its initial translateX(110%)
		// is committed before open() flips it to translateX(0).
		void this.querySelector(".drawer__inner")?.offsetWidth;

		requestAnimationFrame(() => this.open());
	}

	getSectionInnerHTML(html, selector = ".shopify-section") {
		const parsed = new DOMParser().parseFromString(html, "text/html");
		const target = selector
			? parsed.querySelector(selector)
			: parsed.querySelector(".shopify-section");
		return target ? target.innerHTML : "";
	}

	getSectionsToRender() {
		return [
			{
				id: "cart-drawer",
				selector: "#CartDrawer",
			},
			{
				id: "cart-icon-bubble",
			},
		];
	}

	getSectionDOM(html, selector = ".shopify-section") {
		return new DOMParser()
			.parseFromString(html, "text/html")
			.querySelector(selector);
	}

	setActiveElement(element) {
		this.activeElement = element;
	}
}

customElements.define("cart-drawer", CartDrawer);

class CartDrawerItems extends CartItems {
	connectedCallback() {
		this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
			if (event.source === "cart-items" || event.source === "product-form") {
				return;
			}
			this.onCartUpdate();
		});
	}

	onCartUpdate() {
		fetch(`${routes.cart_url}?section_id=cart-drawer`)
			.then((response) => response.text())
			.then((responseText) => {
				const html = new DOMParser().parseFromString(responseText, "text/html");
				const source = html.querySelector("cart-drawer-items");
				if (!source) return;
				this.innerHTML = source.innerHTML;
			})
			.catch((e) => {
				console.error(e);
			});
	}

	getSectionsToRender() {
		return [
			{
				id: "CartDrawer",
				section: "cart-drawer",
				selector: ".drawer__inner",
			},
			{
				id: "cart-icon-bubble",
				section: "cart-icon-bubble",
				selector: ".shopify-section",
			},
		];
	}
}

customElements.define("cart-drawer-items", CartDrawerItems);
